// Placeholder for BranchesPage. Will move content from admin/BranchesPage.js
import React, { useEffect, useState, useCallback } from "react";
import { fetchBranches, editBranch, deleteBranch } from "../services/branchApi";
import { useSelector } from "react-redux";
import AddBranchModal from "../superadmin/branch/AddBranchModal";
import { PlusIcon, WifiIcon } from "@heroicons/react/24/outline";
import BranchTable from "../superadmin/branch/BranchTable";
import BranchDetailsModal from "../superadmin/branch/BranchDetailsModal";
// ...existing code...
import SearchInput from "../components/CommonComponents/SearchInput";
import Pagination from "../components/CommonComponents/Pagination";
import PrimaryButton from "../components/CommonComponents/PrimaryButton";
import EmptyState from "../components/CommonComponents/EmptyState";
import DeleteModal from "../components/CommonComponents/ConfirmationModal";
import { showSuccessToast, showErrorToast } from "../utils/toast";


function BranchesPage() {
    // Detect if user is superadmin (user_type === 0)
    const user = useSelector(state => state.auth.user);
    const isSuperadmin = user && user.user_type === 0;
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const branchesPerPage = 10;
    const [modalState, setModalState] = useState({
        type: null,
        branch: null,
        open: false,
    });

    const loadBranches = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await fetchBranches(undefined, isSuperadmin);
            setBranches(res.data.data || res.data || []);
        } catch (e) {
            setError("Failed to load branches");
        }
        setLoading(false);
    }, [isSuperadmin]);

    useEffect(() => {
        loadBranches();
    }, [loadBranches]);

    const closeModal = () => setModalState({ type: null, branch: null, open: false });
    const handleAddBranch = () => setModalState({ type: "add", branch: null, open: true });
    const handleEdit = (branch) => setModalState({ type: "edit", branch, open: true });
    const handleDelete = (branch) => setModalState({ type: "delete", branch, open: true });

    const confirmDelete = async () => {
        try {
            if (!modalState.branch) return;
            await deleteBranch(modalState.branch.id, isSuperadmin);
            showSuccessToast("Branch deleted successfully");
            loadBranches();
        } catch (err) {
            showErrorToast("Failed to delete branch");
        }
        closeModal();
    };

    const handleToggleRequest = (branch) => setModalState({ type: "toggle", branch, open: true });

    const confirmToggle = async () => {
        try {
            if (!modalState.branch) return;
            const newStatus = modalState.branch.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
            await editBranch(modalState.branch.id, { ...modalState.branch, status: newStatus }, isSuperadmin);
            showSuccessToast(`Branch ${newStatus === "ACTIVE" ? "activated" : "deactivated"} successfully`);
            loadBranches();
        } catch (err) {
            showErrorToast("Failed to update branch status");
        }
        closeModal();
    };

    return (
        <div className="pt-6">
            {/* Search + Add Button */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                <SearchInput
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                    }}
                    placeholder="Search by branch name..."
                />
                <PrimaryButton onClick={handleAddBranch}>+ Add Branch</PrimaryButton>
            </div>

            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold" style={{ color: 'var(--color-text)' }}>Branches List</h2>
            </div>

            {/* Branch Table */}
            {error && (
                <EmptyState
                    icon={WifiIcon}
                    title="Failed to Load Branches"
                    message="Check your internet connection and try again."
                    onRetry={loadBranches}
                    retryLabel="Reload"
                />
            )}
            {!error && loading ? (
                <div className="text-center py-8 text-gray-500">Loading branches...</div>
            ) : !error && (
                <BranchTable
                    branches={branches.filter((b) =>
                        b.name.toLowerCase().includes(searchTerm.toLowerCase())
                    ).slice(
                        (currentPage - 1) * branchesPerPage,
                        currentPage * branchesPerPage
                    )}
                    onViewDetails={branch => {
                        setSelectedBranch(branch);
                        setShowDetailsModal(true);
                    }}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onToggle={handleToggleRequest}
                />
            )}

            {/* Pagination - Only show when no error and data exists */}
            {!error && (
                <div className="flex flex-col items-center gap-2 mt-2">
                    <Pagination
                        currentPage={currentPage}
                        totalPages={Math.max(Math.ceil(branches.filter((b) =>
                            b.name.toLowerCase().includes(searchTerm.toLowerCase())
                        ).length / branchesPerPage), 1)}
                        onPageChange={setCurrentPage}
                    />
                    <p className="text-sm" style={{ color: 'var(--color-text)', opacity: 0.7 }}>
                        {(() => {
                            const filtered = branches.filter((b) =>
                                b.name.toLowerCase().includes(searchTerm.toLowerCase())
                            );
                            const totalPages = Math.max(Math.ceil(filtered.length / branchesPerPage), 1);
                            return `Showing ${filtered.length === 0 ? 0 : (currentPage - 1) * branchesPerPage + 1} – ${Math.min(currentPage * branchesPerPage, filtered.length)} of ${filtered.length} items | Page ${currentPage} of ${totalPages}`;
                        })()}
                    </p>
                </div>
            )}

            {modalState.open && (modalState.type === "add" || modalState.type === "edit") && (
                <AddBranchModal
                    isOpen
                    onClose={closeModal}
                    onBranchAdded={loadBranches}
                    initialData={modalState.type === "edit" ? modalState.branch : null}
                    isEditMode={modalState.type === "edit"}
                    isSuperadmin={isSuperadmin}
                />
            )}
            <BranchDetailsModal
                branch={selectedBranch}
                isOpen={showDetailsModal}
                onClose={() => {
                    setShowDetailsModal(false);
                    setSelectedBranch(null);
                }}
                isSuperadmin={isSuperadmin}
            />
            {modalState.open && modalState.type === "delete" && (
                <DeleteModal
                    isOpen
                    onClose={closeModal}
                    onConfirm={confirmDelete}
                    title="Delete Branch"
                    message={`Are you sure you want to delete "${modalState.branch?.name}"? This action cannot be undone.`}
                    confirmText="Yes"
                    cancelText="No"
                    type="delete"
                />
            )}
            {modalState.open && modalState.type === "toggle" && (
                <DeleteModal
                    isOpen
                    onClose={closeModal}
                    onConfirm={confirmToggle}
                    title="Change Branch Status"
                    message={`Are you sure you want to set this branch to ${modalState.branch?.status === "ACTIVE" ? "INACTIVE" : "ACTIVE"}?`}
                    confirmText="Yes"
                    cancelText="No"
                    type="delete"
                />
            )}
        </div>
    );
}

export default BranchesPage;
